import React, { act, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import toast from "react-hot-toast";

const ALL_AVAILABLE_SERVICES = [
  "IGM",
  "ISN",
  "RSF",
  "MSN",
  "Catalogue Rejection",
  "Logs Monitoring",
];

const STORE_DEFAULT_KEY_NAME_MAP = {
  webhook: "np_communication_webhook",
  settlement_agency_public_key: "settlement_agency_public_key",
  np_subscriber_private_id: "subscriber_private_id",
  np_unique_id: "unique_id",
  np_signing_public_id: "signing_public_id",
};

const getStoreConfigDefaults = (defaultKeysMap = {}) => {
  return Object.entries(STORE_DEFAULT_KEY_NAME_MAP)
    .map(([storeKeyName, defaultKeyName]) => {
      const defaultValue = defaultKeysMap?.[defaultKeyName] || "";
      if (!defaultValue) return null;

      return {
        sKey: storeKeyName,
        sValue: defaultValue,
        prefilled: true,
      };
    })
    .filter(Boolean);
};

const applyNoKeysDefaultToFields = (fields = [], defaultKeysMap = {}) => {
  const noKeysValue = defaultKeysMap?.no_keys || "";
  if (!noKeysValue) return fields;

  return fields.map((field) => {
    if (field.key !== "nokey") return field;
    if (field.value && field.value.trim() !== "") return field;

    return {
      ...field,
      value: noKeysValue,
    };
  });
};

const useTenantDetails = ({ id }) => {
  const { authData } = useSelector((state) => state.loginSlice);
  const dispatch = useDispatch();

  const [loader, setLoader] = useState(false);
  const [tenantData, setTenantData] = useState(null);
  const [activeTab, setActiveTab] = useState("userinfo");
  const [customLoader, setCustomLoader] = useState(false);
  const [storeUsage, setStoreUsage] = useState();
  const [isStandard, setIsStandard] = useState("standard");
  const [plans, setPlans] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [whiteListedDomains, setWhiteListedDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState({});
  const [modulePermissions, setModulePermissions] = useState([]);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [allDomainOptions, setAllDomainOptions] = useState([]);
  const [onboardingDefaultKeys, setOnboardingDefaultKeys] = useState({});

  // --- NEW STATE FOR TENANT STAGES ---
  const [selectedEnvId, setSelectedEnvId] = useState(null);
  const [isPreparingNextStage, setIsPreparingNextStage] = useState(false); // Track if we are filling data for next stage
  const [stageInputData, setStageInputData] = useState({
    fields: [],
    checklist: [],
  });
  // -----------------------------------

  const [formData, setFormData] = useState({
    sType: "userinfo",
    email: "",
    status: "Inactive",
    brandName: "",
    brandLogo: "",
    newBrandLogoFile: null,
    storeConfigKeys: [],
    subscriptionType: 0,
    selectedPlan: null,
    customer_support: { name: "", email: "", phone: "" },
    gro_support: { name: "", email: "", phone: "" },
  });

  const [submitLoader, setSubmitLoader] = useState(false);
  const [selected, setSelected] = useState([]);

  // Helper to find current active environment index
  const getCurrentEnvIndex = (envInfo) => {
    return envInfo?.findIndex((env) => env.isCurrentEnvironment) || 0;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData((prev) => ({ ...prev, sType: tab }));
    // Reset preparation mode when switching main tabs
    if(tab !== "tenantStages") setIsPreparingNextStage(false);
  };

  const handleCheckboxChange = (id) => {
    setSelected((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    );
  };

  const handleServiceToggle = (serviceName) => {
    setSelectedServices((prev) =>
      prev.includes(serviceName)
        ? prev.filter((s) => s !== serviceName)
        : [...prev, serviceName]
    );
  };

  const fetchOnboardingDefaultKeys = async () => {
    try {
      const response = await apiHandler({
        url: endpoint.GET_ONBOARD_OPTIONS,
        method: "GET",
        dispatch,
        authToken: authData,
      });

      if (response?.data?.success) {
        const defaultKeys = response?.data?.data?.default_keys || [];
        const normalizedDefaultKeys = defaultKeys.reduce((acc, item) => {
          if (item?.key_name) {
            acc[item.key_name] = item.key_value || "";
          }
          return acc;
        }, {});

        setOnboardingDefaultKeys(normalizedDefaultKeys);
      }
    } catch (error) {
    }
  };

  const getTenantById = async () => {
    try {
      setLoader(true);
      const response = await apiHandler({
        url: `${endpoint.GET_TENANT_BY_ID}/${id}`,
        method: "GET",
        dispatch,
        authToken: authData,
      });

      if (response.data.success) {
        const tenant = response.data.data.tenant;
        const domainOptions = response.data.data.SellerDomainsOptions || [];
        setAllDomainOptions(domainOptions);

        const csData = tenant.customer_support?.[0];
        const groData = tenant.gro_support?.[0];
        const defaultStoreConfigKeys = getStoreConfigDefaults(onboardingDefaultKeys);

        // Extract both parent services and nested sub-services
        const extractAllServices = (activeServices) => {
          const allServices = [];
          if (activeServices) {
            Object.entries(activeServices).forEach(([parentService, subServices]) => {
              allServices.push(parentService);
              // Add nested sub-services if they exist
              if (Array.isArray(subServices) && subServices.length > 0) {
                allServices.push(...subServices);
              }
            });
          }
          return allServices;
        };

        const formattedData = {
          tenantId: tenant.iTenantId,
          brandName: tenant.sBrandName,
          email: tenant.sTenantEmail,
          status: tenant.sStatus,
          joinedAt: tenant.joinedAt,
          npType: tenant.npType,
          domain: tenant.domain,
          environment_name: tenant.environment_name,
          sStatus: tenant.sStatus?.toLowerCase(),
          cmsURL: tenant.sCMSURL,
          brandLogo: tenant.sBrandLogo,
          selectedRetailDomains: tenant.SelectedRetailDomains || [],
          raw_customer_support: tenant.customer_support,
          raw_gro_support: tenant.gro_support,
          storeDetails: tenant?.storeDetails
            ? [
                {
                  typeName: tenant.storeDetails.storeTypeName,
                  type: tenant.storeDetails.storeType,
                  subscriptionChangeable:
                    tenant.storeDetails.subscriptionChangeable,
                  keys: tenant.storeDetails.storeKeys || [],
                  domains: tenant.SelectedRetailDomains || [],
                },
              ]
            : [],
          activeServices: extractAllServices(tenant?.activeServices || {}),
          environmentInfo: tenant.EnvInfo, // Capture Environment Info
          users:
            tenant.users?.map((user) => ({
              sName: user.sName,
              sEmail: user.sEmail,
              dtCreatedAt: user.dtCreatedAt,
              dtUpdatedAt: user.dtUpdatedAt,
              bSubscribedNotification: user.bSubscribedNotification,
              bIsActive: user.bIsActive,
              sRole: user.sRole,
            })) || [],
          activeSubscription:
            tenant.activeSubscription?.map((sub) => ({
              billingCycle: sub.billingCycleName,
              planStartDate: sub.planStartDate,
              planEndDate: sub.planEndDate,
              planType: sub.planType,
              planName: sub.planName,
              price: sub.dPrice,
              iPlanVersionId: sub.iPlanVersionId,
              iBillingCycleId: sub.iBillingCycleId,
              features: sub.Features.reduce((acc, feature) => {
                acc[feature.sFieldName] = feature.sValue;
                return acc;
              }, {}),
            })) || [],
          subscriptionHistory:
            tenant.subscriptionHistory?.map((history) => ({
              subscriptionName: history.subscriptionName,
              dtStartDate: history.dtStartDate,
              dtEndDate: history.dtEndDate,
              dtCreatedAt: history.dtCreatedAt,
              dAmount: history.dAmount,
            })) || [],
          whitelistedDomains: tenant.whitelisteddomains,
          selectedPermissions: tenant.selectedPermissions,
          msnCommission: tenant.msn_commission || null,
        };

        const selectedIds = tenant?.selectedPermissions
          ? tenant?.selectedPermissions?.map((perm) => perm.iModulePermissionId)
          : [];

        setInitialPermissions(selectedIds);
        setSelected(selectedIds);
        setModulePermissions(tenant.modulePermissions);
        setTenantData(formattedData);
        setSelectedServices(formattedData.activeServices);

        setFormData((prev) => ({
          ...prev,
          email: tenant.sTenantEmail || "",
          status: tenant.sStatus || "Inactive",
          selectedPlan: tenant.activeSubscription?.[0]?.iPlanVersionId || null,
          subscriptionType:
            tenant.activeSubscription?.[0]?.iBillingCycleId || 0,
          brandName: tenant.sBrandName || "",
          brandLogo: tenant.sBrandLogo || "",
          retailDomains:
            tenant.SelectedRetailDomains?.map((d) => d.value) || [],
          storeConfigKeys:
            (() => {
              const mergedStoreKeysMap = new Map(
                defaultStoreConfigKeys.map((key) => [key.sKey, key]),
              );

              (tenant.storeDetails?.storeKeys || []).forEach((key) => {
                const existingDefault = mergedStoreKeysMap.get(key.sKey);
                mergedStoreKeysMap.set(key.sKey, {
                  sKey: key.sKey,
                  sValue:
                    key.sValue || existingDefault?.sValue || "",
                  prefilled:
                    key.sValue?.trim() ? true : !!existingDefault?.prefilled,
                });
              });

              return Array.from(mergedStoreKeysMap.values());
            })(),
          customer_support: {
            name: csData?.name || "",
            email: csData?.email || "",
            phone: csData?.phone || "",
          },
          gro_support: {
            name: groData?.name || "",
            email: groData?.email || "",
            phone: groData?.phone || "",
          },
        }));

        // --- STAGE INIT LOGIC ---
        // Reset preparation state on reload
        setIsPreparingNextStage(false); 
        
        if (tenant.EnvInfo && tenant.EnvInfo.length > 0) {
          const currentEnv = tenant.EnvInfo.find((e) => e.isCurrentEnvironment);
          if (currentEnv) {
            setSelectedEnvId(currentEnv.environment_id);
            setStageInputData({
              fields: applyNoKeysDefaultToFields(
                currentEnv.fields || [],
                onboardingDefaultKeys,
              ),
              checklist: currentEnv.checklist || [],
            });
          } else {
            setSelectedEnvId(tenant.EnvInfo[0].environment_id);
            setStageInputData({
                fields: applyNoKeysDefaultToFields(
                  tenant.EnvInfo[0].fields || [],
                  onboardingDefaultKeys,
                ),
                checklist: tenant.EnvInfo[0].checklist || [],
              });
          }
        }
      }
    } catch (error) {
   
    } finally {
      setLoader(false);
    }
  };

  const handleToggleActive = async (domain, currentStatus) => {
    // ... [Existing Logic]
    try {
        setSelectedDomain(domain);
        const newStatus = !currentStatus;
        let newDomain = { ...domain, bActive: newStatus };
  
        const catagory = tenantData.whitelistedDomains.map((item) => ({
          ...item,
          bActive: item.iDomainId === domain.iDomainId ? newStatus : item.bActive,
        }));
  
        const originalSType = formData.sType;
        setFormData((prev) => ({ ...prev, sType: "domain" }));
        await updateTenantDetails(newDomain);
        setFormData((prev) => ({ ...prev, sType: originalSType }));
      } catch (error) {
        toast.error("Failed to update domain status.");
      }
  };

  const updateTenantDetails = async (domain = null, statusOverride = null) => {
    // ... [Existing Update Logic - No changes needed here for stages]
    if (!tenantData?.tenantId || !formData.sType) {
        alert("Invalid tenant data or type. Please check your inputs.");
        return;
      }
  
      const formDataToSend = new FormData();
      formDataToSend.append("iTenantId", tenantData.tenantId);
  
      // FIXED LOGIC: If statusOverride exists, we are toggling, so force "userstatus"
      let typeToSend;
      if (statusOverride !== null) {
        typeToSend = "userstatus";
      } else if (formData.sType === "activeservices") {
        typeToSend = "services";
      } else {
        typeToSend = formData.sType;
      }
  
      formDataToSend.append("type", typeToSend);
      
      // Determine which switch case to run based on the resolved typeToSend
      switch (typeToSend) {
        case "userstatus":
          formDataToSend.append("status", statusOverride);
          break;
  
        case "userinfo":
          formDataToSend.append("sEmail", formData.email);
          formDataToSend.append("sStatus", formData.status);
          break;
        case "subscription":
          formDataToSend.append("iPlanVersionId", formData.selectedPlan || null);
          formDataToSend.append(
            "iBillingCycleId",
            formData.subscriptionType || null
          );
          break;
        case "domain":
          if (domain) {
            formDataToSend.append("sDomain", domain.sDomain);
            formDataToSend.append("iDomainId", domain.iDomainId);
            formDataToSend.append("bActive", domain.bActive);
          } else if (
            whiteListedDomains.length > 0 &&
            whiteListedDomains[0].sDomain
          ) {
            formDataToSend.append("sDomain", whiteListedDomains[0].sDomain);
            formDataToSend.append("iDomainId", "");
            formDataToSend.append("bActive", true);
          } else {
            toast.error("No domain information provided to update.");
            return;
          }
          break;
        case "permission":
          const newlyAddedPermissions = selected
            .filter((id) => !initialPermissions.includes(id))
            .map((id) => {
              return { iModulePermissionId: id };
            });
          const deletedPermissionIds = initialPermissions
            .filter((id) => !selected.includes(id))
            .map((id) => {
              return { iModulePermissionId: id };
            });
  
          formDataToSend.append(
            "removedPermissions",
            JSON.stringify(deletedPermissionIds)
          );
          formDataToSend.append(
            "addedPermissions",
            JSON.stringify(newlyAddedPermissions)
          );
  
          break;
  
        case "services":
          formDataToSend.append("services", JSON.stringify(selectedServices));
  
          const customerSupportPayload = {
            name: formData.customer_support.name || "",
            email: formData.customer_support.email || "",
            phone: formData.customer_support.phone || "",
          };
          const groSupportPayload = {
            name: formData.gro_support.name || "",
            email: formData.gro_support.email || "",
            phone: formData.gro_support.phone || "",
          };
  
          formDataToSend.append(
            "customer_support",
            JSON.stringify(customerSupportPayload)
          );
          formDataToSend.append("gro_support", JSON.stringify(groSupportPayload));
  
          break;
        case "storeDetails":
          formDataToSend.append("brandName", formData.brandName);
  
          if (formData.newBrandLogoFile) {
            formDataToSend.append("sBrandLogo", formData.newBrandLogoFile);
          } else {
            formDataToSend.append("sBrandLogoURL", formData.brandLogo);
          }
  
          const storeKeysPayload = formData.storeConfigKeys.map((key) => ({
            sKey: key.sKey,
            sValue: key.sValue,
          }));
  
          formDataToSend.append("storeKeys", JSON.stringify(storeKeysPayload));
  
          const retailDomainsPayload = formData.retailDomains || [];
          formDataToSend.append("retailDomains", retailDomainsPayload.join(","));
  
          break;
  
        default:
          alert("Invalid update type.");
          return;
      }
  
      try {
        setSubmitLoader(true);
  
        const response = await apiHandler({
          url: `${endpoint.UPDATE_TENANT}/${tenantData.tenantId}`,
          method: "PUT",
          data: formDataToSend,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          dispatch,
          authToken: authData,
        });
        if (formData.sType === "domain" && !domain) {
          // Clear input only if adding new
          setWhiteListedDomains([]);
        }
        setSelectedDomain({});
        if (response?.data?.success) {
          toast.success("Tenant details updated successfully!");
          await getTenantById(); // Refresh data after update
  
          handleCloseModal(); // Close modal
        } else {
          toast.error(response?.data?.message || "Failed to update tenant details.");
        }
      } catch (error) {
 
      } finally {
        setSubmitLoader(false);
      }
  };

  // ... [Existing event handlers: handleLogoUpload, handleSupportFieldChange, etc.]
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setFormData((prevFormData) => ({
        ...prevFormData,
        brandLogo: previewURL,
        newBrandLogoFile: file,
      }));
    }
  };

  const handleSupportFieldChange = (supportType, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [supportType]: {
        ...prev[supportType],
        [field]: value,
      },
    }));
  };

const handleTenantStatusToggle = async () => {
  if (!tenantData) return;

  const newStatus = tenantData.sStatus === "active" ? "inactive" : "active";

  // 🔥 Optimistic update
  setTenantData((prev) => ({
    ...prev,
    sStatus: newStatus,
  }));

  await updateTenantDetails(null, newStatus);
};

  const handleFormUpdate = (field, value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [field]: value,
    }));
  };

  const handleStoreKeyChange = (keyName, value) => {
    setFormData((prev) => {
      const existingIndex = prev.storeConfigKeys.findIndex(
        (k) => k.sKey === keyName
      );
      let updatedKeys = [...prev.storeConfigKeys];

      if (existingIndex > -1) {
        updatedKeys[existingIndex] = {
          ...updatedKeys[existingIndex],
          sValue: value,
        };
      } else {
        updatedKeys.push({ sKey: keyName, sValue: value, prefilled: false });
      }

      return { ...prev, storeConfigKeys: updatedKeys };
    });
  };

  useEffect(() => {
    fetchOnboardingDefaultKeys();
    getTenantById();
  }, [id]);

  useEffect(() => {
    if (!onboardingDefaultKeys || Object.keys(onboardingDefaultKeys).length === 0) {
      return;
    }

    setFormData((prev) => {
      const defaultStoreConfigKeys = getStoreConfigDefaults(onboardingDefaultKeys);
      const mergedStoreKeysMap = new Map(
        (prev.storeConfigKeys || []).map((key) => [key.sKey, key]),
      );

      defaultStoreConfigKeys.forEach((defaultKey) => {
        const existing = mergedStoreKeysMap.get(defaultKey.sKey);
        if (!existing) {
          mergedStoreKeysMap.set(defaultKey.sKey, defaultKey);
          return;
        }

        if (!existing.sValue || existing.sValue.trim() === "") {
          mergedStoreKeysMap.set(defaultKey.sKey, {
            ...existing,
            sValue: defaultKey.sValue,
            prefilled: true,
          });
        }
      });

      return {
        ...prev,
        storeConfigKeys: Array.from(mergedStoreKeysMap.values()),
      };
    });

    setStageInputData((prev) => ({
      ...prev,
      fields: applyNoKeysDefaultToFields(prev.fields || [], onboardingDefaultKeys),
    }));
  }, [onboardingDefaultKeys]);

  const fetchPlans = async () => {
    try {
      setCustomLoader(true);
      let url = `${endpoint.GET_PLAN_BILLINGCYCLES}?isStandard=${
        isStandard === "standard" ? 1 : 0
      }`;
      const response = await apiHandler({
        url: url,
        method: "GET",
        authToken: authData,
        dispatch,
      });
      if (response.data.success) {
        setPlans(response.data.data);
      } else {
        toast.error("Failed to fetch subscription plans.");
      }
    } catch (error) {
 
    } finally {
      setCustomLoader(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [isStandard]);

  const handleIsStandardChange = (e) => {
    setIsStandard(e.target.value);
    setFormData((prev) => ({ ...prev, selectedPlan: null }));
  };
  const handleOpenModal = () => {
    // fetchPlans();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setErrors({});
  };

  const handlePlanSelection = (planId) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      selectedPlan: planId,
    }));
  };

  const handleSubscriptionTypeSelection = (type) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      subscriptionType: type,
      selectedPlan: null,
    }));
  };

  const handleAddDomain = () => {
    setWhiteListedDomains((prev) => {
      return [
        ...prev,
        {
          sDomain: "",
        },
      ];
    });
  };

  const handleRemoveDomain = (index) => {
    const newDomains = whiteListedDomains.filter((_, i) => i !== index);
    setWhiteListedDomains(newDomains);
  };

  const handleInputChange = (e, index) => {
    const { value } = e.target;

    const domains = [...whiteListedDomains];
    domains[index] = { ...domains[index], sDomain: value };

    setWhiteListedDomains(domains);
  };

  // --- STAGE HANDLERS ---

  const handleStageTabClick = (envId) => {
    const targetEnv = tenantData.environmentInfo.find(
      (e) => e.environment_id === envId
    );
    if (targetEnv) {
      setSelectedEnvId(envId);
      // If user clicks manually on tabs, show readonly or editable based on active status
      // Reset prep mode if they click away
      setIsPreparingNextStage(false);
      
      setStageInputData({
        fields: applyNoKeysDefaultToFields(
          targetEnv.fields || [],
          onboardingDefaultKeys,
        ),
        checklist: targetEnv.checklist || [],
      });
    }
  };

  const handleStageFieldChange = (key, value) => {
    setStageInputData((prev) => ({
      ...prev,
      fields: prev.fields.map((field) =>
        field.key === key ? { ...field, value: value } : field
      ),
    }));
  };

  const handleStageChecklistToggle = (key) => {
    setStageInputData((prev) => ({
      ...prev,
      checklist: prev.checklist.map((item) =>
        item.key === key ? { ...item, isDone: !item.isDone } : item
      ),
    }));
  };

  const updateStageInfo = async () => {
    if (!selectedEnvId) return;

    // Payload for regular Update (Same stage)
    const payload = {
      stage: selectedEnvId,
      stageFields: stageInputData.fields
        .filter((f) => f.key === "nokey") // Assuming only NO KEY is updated here generally? Or allow all?
        .map((f) => ({
          sKey: f.key,
          sValue: f.value || "",
        })),
      checklistFields: stageInputData.checklist.map((c) => ({
        sKey: c.key,
        bValue: c.isDone,
      })),
    };

    try {
      setSubmitLoader(true);
      const response = await apiHandler({
        url: `${endpoint.UPDATE_TENANT_STAGE}/${tenantData.tenantId}`,
        method: "PUT",
        data: payload,
        dispatch,
        authToken: authData,
      });

      if (response.data.success) {
        toast.success("Stage information saved successfully!");
        await getTenantById();
      } else {
        toast.error(response.data.message || "Failed to update stage info.");
      }
    } catch (error) {
      toast.error("An error occurred while updating.");
    } finally {
      setSubmitLoader(false);
    }
  };

  // --- NEW: Step 1 - Prepare for Next Stage (Local Switch) ---
  const handlePrepareNextStage = () => {
     // 1. Validation: Check if all checklist items of CURRENT stage are done
     const pendingItems = stageInputData.checklist.filter(item => !item.isDone);
     if(pendingItems.length > 0) {
         toast.error("Please complete all checklist items before moving to the next stage.");
         return;
     }

     // 2. Find Next Stage
     const currentIndex = tenantData.environmentInfo.findIndex(e => e.environment_id === selectedEnvId);
     if (currentIndex === -1 || currentIndex === tenantData.environmentInfo.length - 1) {
         toast.error("No next stage available.");
         return;
     }

     const nextStageEnv = tenantData.environmentInfo[currentIndex + 1];
     
     // 3. Switch UI to Next Stage
     setSelectedEnvId(nextStageEnv.environment_id);
     setStageInputData({
         fields: applyNoKeysDefaultToFields(
           nextStageEnv.fields || [],
           onboardingDefaultKeys,
         ),
         checklist: nextStageEnv.checklist || []
     });
     
     // 4. Enable Edit Mode for this future stage
     setIsPreparingNextStage(true);
  };


  // --- NEW: Step 2 - Activate Stage (API Call) ---
  const activateNextStage = async () => {
    if (!selectedEnvId) return;

    // Construct fields object from the NOW filled data
    const fieldsObj = stageInputData.fields.reduce((acc, field) => {
        acc[field.key] = field.value || "";
        return acc;
    }, {});

    // Construct checklist object
    const checklistObj = stageInputData.checklist.reduce((acc, item) => {
        acc[item.key] = item.isDone;
        return acc;
    }, {});

    const payload = {
        stages: {
            currentStage: selectedEnvId, // Activating THIS stage
            fields: fieldsObj,
            checklist: checklistObj
        }
    };

    try {
        setSubmitLoader(true);
        const response = await apiHandler({
            url: `${endpoint.ADD_NEW_STAGE_INFO}/${tenantData.tenantId}`, 
            method: "POST", 
            data: payload,
            dispatch,
            authToken: authData,
        });

        if (response.data.success) {
            toast.success("New Stage Activated Successfully!");
            setIsPreparingNextStage(false); // No longer preparing
            await getTenantById(); // Refresh everything
        } else {
            toast.error(response.data.message || "Failed to activate stage.");
        }
    } catch (error) {
        toast.error("An error occurred while activating stage.");
    } finally {
        setSubmitLoader(false);
    }
  };

  return {
    // ... [Existing exports]
    loader,
    tenantData,
    activeTab,
    setActiveTab,
    formData,
    setFormData,
    handleFormUpdate,
    updateTenantDetails,
    handleToggleActive,
    handleLogoUpload,
    plans,
    isStandard,
    setIsStandard,
    customLoader,
    handleOpenModal,
    handleCloseModal,
    isModalOpen,
    errors,
    setErrors,
    handleIsStandardChange,
    handlePlanSelection,
    handleSubscriptionTypeSelection,
    submitLoader,
    handleTabChange,
    storeUsage,
    handleAddDomain,
    whiteListedDomains,
    handleRemoveDomain,
    handleInputChange,
    submitLoader,
    modulePermissions,
    selected,
    handleCheckboxChange,
    ALL_AVAILABLE_SERVICES,
    selectedServices,
    handleServiceToggle,
    handleStoreKeyChange,
    handleSupportFieldChange,
    handleTenantStatusToggle,
    allDomainOptions,
    onboardingDefaultKeys,
    // --- STAGE EXPORTS ---
    selectedEnvId,
    stageInputData,
    isPreparingNextStage,
    handleStageTabClick,
    handleStageFieldChange,
    handleStageChecklistToggle,
    updateStageInfo,
    handlePrepareNextStage, // Logic to switch tab
    activateNextStage, // Logic to call API
    getCurrentEnvIndex,
  };
};

export default useTenantDetails;